import { useState } from 'react'
import styled from 'styled-components'

import CandidateCard from '@/app/2023/consejo/candidatos/components/CandidatesMesh/CandidateCard'
import reportAnalytics from '@/app/2023/consejo/components/analyticsEvents'
import { TCircleEventReturn } from '@/app/components/CircleMesh/CircleMesh'
import Popover from '@/app/components/Popover'
import useWindowDimensions from '@/app/hooks/useWindowDimensions'
import { spaces } from '@/config/global-parameters'
import { normalNumberFormatter, percentFormatter } from '@/helpers/formatters'
import { showInDesktop, showInMobile } from '@/styles/mediaQueries'

import { IGanadorWithData } from '../../types/ganadores'
import CandidateSheetBase from './CandidateSheet'
import CircleGraphBase from './CircleGraph'

interface IGanadoresGraph {
  className?: string
  ganadores: IGanadorWithData[]
  popoverPlace?: 'bottom' | 'right'
  popoverMode?: 'vertical' | 'horizontal'
  nRowsMobile?: number
  nRowsDesktop?: number
}

const GanadoresGraph = ({
  className,
  ganadores,
  nRowsDesktop = 2,
  nRowsMobile = 3,
  popoverMode,
  popoverPlace,
}: IGanadoresGraph) => {
  const [selectedCandidate, setSelectedCandidate] = useState<IGanadorWithData>()
  const { isBigScreen, isMobile } = useWindowDimensions()

  const idealPopoverPlace = isBigScreen ? 'right' : 'bottom'
  const idealPopoverMode = isBigScreen ? 'vertical' : 'horizontal'

  const handleOnCircleClickDesktop = (candidate: TCircleEventReturn<IGanadorWithData>) => {
    setSelectedCandidate(candidate?.data)
    reportAnalytics(
      'consejeros_candidate_circle_click',
      candidate?.data ? candidate.data.nombre_completo : ''
    )
  }

  const handleOnCircleEnterDesktop = (candidate: TCircleEventReturn<IGanadorWithData>) => {
    setSelectedCandidate(candidate?.data)
    reportAnalytics(
      'consejeros_candidate_circle_mouse_enter',
      candidate?.data ? candidate.data.nombre_completo : ''
    )
  }

  const handleOnCircleLeaveDesktop = (candidate?: TCircleEventReturn<IGanadorWithData>) => {
    reportAnalytics(
      'consejeros_candidate_circle_mouse_leave',
      candidate?.data ? candidate.data.nombre_completo : ''
    )
  }

  const handleOnCircleClickMobile = (candidate: TCircleEventReturn<IGanadorWithData>) => {
    setSelectedCandidate(candidate?.data)
    reportAnalytics(
      'consejeros_candidate_circle_click',
      candidate?.data ? candidate.data.nombre_completo : ''
    )
  }

  const handleCandidateCardClose = (candidate?: TCircleEventReturn<IGanadorWithData>) => {
    setSelectedCandidate(undefined)
    reportAnalytics(
      'consejeros_candidate_card_close',
      candidate?.data ? candidate.data.nombre_completo : ''
    )
  }

  return (
    <Wrapper className={className}>
      <Popover
        afterHide={() => setSelectedCandidate(undefined)}
        anchor={
          <Anchor>
            <CircleGraph
              candidates={ganadores}
              gap="min(0.3vw, 4px)"
              nRows={nRowsDesktop}
              onCircleClick={handleOnCircleClickDesktop}
              onCircleMouseEnter={handleOnCircleEnterDesktop}
              onCircleMouseLeave={handleOnCircleLeaveDesktop}
              selectedCandidate={selectedCandidate}
            />
            <CircleGraphMobile
              candidates={ganadores}
              gap="min(0.3vw, 4px)"
              nRows={nRowsMobile}
              onCircleClick={handleOnCircleClickMobile}
            />
          </Anchor>
        }
        clickable={true}
        disabled={isMobile}
        noArrow={true}
        place={popoverPlace ? popoverPlace : idealPopoverPlace}
      >
        {selectedCandidate && (
          <CandidateCard
            cantidadVotos={normalNumberFormatter.format(selectedCandidate.votos)}
            disableTwitter={true}
            mode={popoverMode ? popoverMode : idealPopoverMode}
            porcentajeVotos={percentFormatter.format(Number(selectedCandidate.porcentaje) || 0)}
            {...selectedCandidate}
          />
        )}
      </Popover>
      {selectedCandidate && isMobile && (
        <CandidateSheet
          candidato={selectedCandidate}
          onClose={handleCandidateCardClose}
        />
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`
const CircleGraph = styled(CircleGraphBase)`
  width: 100%;
  ${showInDesktop}
`
const CircleGraphMobile = styled(CircleGraphBase)`
  max-width: ${spaces.mobileBreakpoint};
  width: 100%;
  ${showInMobile}
`
const CandidateSheet = styled(CandidateSheetBase)`
  ${showInMobile}
`

const Anchor = styled.div`
  width: 100%;
  max-width: 830px;
  display: flex;
  justify-content: center;
`

export default GanadoresGraph
